import {deepMerge} from '../../utils/OptionsUtils';
import {DeepPartial} from '../../utils/Types';

/**
 * Configuration options for the ObjectDetector.
 */
export class ObjectsOptions {
  debugging = false;
  enabled = false;
  showDebugVisualizations = false;

  /**
   * Minimum delay in milliseconds between continuous object detection runs.
   * A value of 0 runs again as soon as the previous detection finishes.
   */
  pollingIntervalMs = 0;

  /**
   * Margin to add when cropping the object image, as a percentage of image
   * size.
   */
  objectImageMargin = 0.05;

  /**
   * Configuration for the detection backends.
   */
  backendConfig = {
    /** The active backend to use for detection. */
    activeBackend: 'gemini' as 'gemini' | 'mediapipe',
    gemini: {
      systemInstruction: `Please provide me with the bounding box coordinates for the primary objects in the given image, prioritizing objects that are nearby. For each bounding box, include ymin, xmin, ymax, and xmax. These coordinates should be absolute values ranging from 0 to 1000, corresponding to the image as if it were resized to 1000x1000 pixels. The origin (xmin:0; ymin:0) is the top-left corner of the image, and (xmax:1000; ymax:1000) is the bottom-right corner. List a maximum of 5 objects. Ignore hands and other human body parts, as well as any UI elements attached to them (e.g., a blue circle attached to a finger).`,
      /**
       * Extra Gemini generation config merged into the per-call config (over
       * the SDK defaults). Use to pin sampling parameters such as
       * `temperature: 0` for deterministic detections.
       */
      generationConfig: {} as Record<string, unknown>,
      responseSchema: {
        type: 'ARRAY',
        items: {
          type: 'OBJECT',
          required: ['objectName', 'ymin', 'xmin', 'ymax', 'xmax'],
          properties: {
            objectName: {type: 'STRING'},
            ymin: {type: 'NUMBER'},
            xmin: {type: 'NUMBER'},
            ymax: {type: 'NUMBER'},
            xmax: {type: 'NUMBER'},
          },
        },
      },
    },
    /** Configuration for MediaPipe backend. */
    mediapipe: {
      wasmFilesUrl:
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.34/wasm',
      // Check https://ai.google.dev/edge/mediapipe/solutions/vision/object_detector#models for other models.
      modelAssetPath:
        'https://storage.googleapis.com/mediapipe-models/object_detector/efficientdet_lite2/int8/latest/efficientdet_lite2.tflite',
      scoreThreshold: 0.5,
    },
  };

  constructor(options?: DeepPartial<ObjectsOptions>) {
    if (options) {
      deepMerge(this, options);
    }
  }

  /**
   * Enables the object detector.
   */
  enable() {
    this.enabled = true;
    return this;
  }
}
